import { safeSessionStorage } from 'wsm-tagging-utils';
import { getCallsourceSessionId } from './callsourceSession.js';

// Pixall only propagates events whose metadata carries `element`, `action` and
// `tagIndex`, so these values are what carry the session ID to Pixall. `element`
// and `action` are governed pick-lists; `tagIndex` is assigned by CDL governance
// and must differ per event shape, so the change event (US2129978) has its own.
// Values confirmed for US2134734 by Anna Bonatz (element/action, tagIndex style),
// Nate Foster (tagIndex value) and Michelle Westbay (structure), 2026-10-08.
export const SESSION_ID_ACQUIRED_EVENT = {
	element: 'Link',
	action: 'Impression',
	tagIndex: 'ddcc-sct-001',
	intention: 'CallSource Session ID Acquired',
	category: 'CallSource'
};

// Shares `element` / `action` with the first-acquisition event above, as agreed
// with CDL governance; `intention` and `tagIndex` tell the two apart - any
// difference in payload needs its own tagIndex.
export const SESSION_ID_CHANGED_EVENT = {
	element: 'Link',
	action: 'Impression',
	tagIndex: 'ddcc-sct-002',
	intention: 'CallSource Session ID Changed',
	category: 'CallSource'
};

// The last callsource session ID this visit has seen. Kept in session storage so
// reloads and navigation within the visit compare against it, and so the event
// fires only when the ID actually changes (e.g. after CallSource expires it).
export const LAST_SESSION_ID_KEY = 'callsourceSessionIdLastSeen';

// In-memory copy for when session storage is blocked, and so repeated calls on
// one page load never push the event twice for the same ID.
let lastSessionIdInMemory = null;

// Fire the event on first acquisition per visit, not per page load. CDL restores
// user data (including sessionizedCallTrackingId) from sessionStorage on later page
// loads of an opted-in visit, so without this guard every page of the visit would
// push the event again.
const EVENT_FIRED_KEY = 'callsourceSessionIdEventFired';

const report = (msg, tcdcmpid, extra = {}) => {
	if (window.newrelic) {
		window.newrelic.addPageAction('callsource', {
			tcdcmpid,
			callsourceStatus: msg,
			...extra
		});
	}
};

/**
 * Pushes SESSION_ID_CHANGED_EVENT when the callsource session ID differs from
 * the last one seen in this visit.
 *
 * The first ID seen is only recorded, not fired on - first acquisition is a
 * separate event (US2134734). With storage blocked the previous ID only lives
 * in memory, so the event fires only for a change within one page load; across
 * page loads there is nothing to compare against, and firing anyway would claim
 * a change that did not happen. Those visitors still get the current ID to
 * Pixall from the first-acquisition event, which fires on every page load when
 * its own guard cannot persist.
 */
const fireIfSessionIdChanged = (callsourceSessionId, tcdcmpid) => {
	// safeSessionStorage returns null when storage is blocked.
	const previousSessionId =
		lastSessionIdInMemory || safeSessionStorage.getItem(LAST_SESSION_ID_KEY);

	if (previousSessionId === callsourceSessionId) {
		return;
	}

	if (previousSessionId !== null) {
		const ids = {
			callsourceSessionId,
			previousCallsourceSessionId: previousSessionId
		};
		// Caught here rather than by initAiIdExchange's catch, which would report
		// an exchange failure even though the exchange succeeded. Returning
		// without recording keeps the retry on the next page load.
		try {
			// A copy, not the constant itself - see the acquired-event push below.
			// This event can fire repeatedly within a visit, so a polluted constant
			// would emit a stale timestamp and a duplicate event id on later fires.
			window.coxAutoDataLayer.push({
				user: {
					sessionizedCallTrackingId: callsourceSessionId
				},
				event: { ...SESSION_ID_CHANGED_EVENT }
			});
		} catch (_e) {
			report('session ID changed push failed', tcdcmpid, ids);
			return;
		}
		report('session ID changed', tcdcmpid, ids);
	}

	// Recorded only after the push lands, so a failed push is retried on the
	// next page load instead of being treated as already delivered.
	lastSessionIdInMemory = callsourceSessionId;
	safeSessionStorage.setItem(LAST_SESSION_ID_KEY, callsourceSessionId);
};

// PhoneSwapping passes its own tcdcmpid (already stripped of '&tcdadid=...') so both callers of the cached
// getCallsourceSessionId() send the same value.
export const initAiIdExchange = async (tcdcmpidFromPhoneSwapping) => {
	const tcdcmpidFromSession = safeSessionStorage.getItem('tcdcmpid');

	if (tcdcmpidFromSession) {
		report('tcdcmpid found in session storage', tcdcmpidFromSession);
	}

	const tcdcmpid =
		tcdcmpidFromPhoneSwapping !== undefined
			? tcdcmpidFromPhoneSwapping
			: new URLSearchParams(window.location.search).get('tcdcmpid') ||
				tcdcmpidFromSession;

	// Not gated on tcdcmpid - getCallsourceSessionId() works whether or not
	// this page arrived from a TCD ad click. tcdcmpid is passed through when
	// we have it (preserves the existing ad-campaign linkage), omitted otherwise.
	if (!window.aiIdExchange) {
		return;
	}

	try {
		const callsourceSessionId = await getCallsourceSessionId(tcdcmpid);
		if (!callsourceSessionId) {
			throw new Error('callsourceSessionId is undefined');
		} else {
			report('aiIdExchange success', tcdcmpid, { callsourceSessionId });
		}
		// The event rides along on the same push as the ID rather than being a second
		// push: a CDL push sends the whole data layer state alongside the event
		// metadata, so one push both stores the ID and delivers it to Pixall. The ID
		// stays in `user` (state that persists for the visit) rather than in `event`,
		// whose values are not retained for later calls.
		const isFirstAcquisition = !safeSessionStorage.getItem(EVENT_FIRED_KEY);

		// A copy, not the constant itself: CDL normalises the pushed event in place
		// (observed on nonprod adding `utcTimestamp`, `timezoneOffset` and `id`), so
		// pushing the constant by reference would pollute it for the rest of the page
		// load and any later push reusing it.
		window.coxAutoDataLayer.push({
			...(isFirstAcquisition && { event: { ...SESSION_ID_ACQUIRED_EVENT } }),
			user: {
				sessionizedCallTrackingId: callsourceSessionId
			}
		});

		// Set only after the push lands, so a failed push is retried on the next page
		// load instead of being recorded as already fired.
		if (isFirstAcquisition) {
			safeSessionStorage.setItem(EVENT_FIRED_KEY, 'true');
		}

		fireIfSessionIdChanged(callsourceSessionId, tcdcmpid);
	} catch (_e) {
		window?.DDC?.log('error', 'aiIdExchange failed');
		report('aiIdExchange failed', tcdcmpid);
	}
};

// Exposed for test isolation only - simulates a fresh page load by dropping the
// in-memory copy, the way a new document would.
export const resetSessionIdChangeState = () => {
	lastSessionIdInMemory = null;
};
